/* eslint-disable prettier/prettier */
import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { UserService } from '../../../shared/services/user/user.service';
import { AuthService } from '../../auth.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { map, shareReplay, startWith } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { StateComplaintService } from '../../../my-tools/dispute-resolution/state-complaint/shared/services/state-complaint.service';
import { MediationService } from '../../../my-tools/dispute-resolution/mediation/mediation-shared/services/mediation.service';

@Component({
  selector: 'app-interactive-access-login',
  templateUrl: './interactive-access-login.component.html',
  styleUrls: ['./interactive-access-login.component.scss'],
  standalone: false,
})
export class InteractiveAccessLoginComponent implements OnInit {
  isBusy = false;
  loginFailed = false;
  isAccountLocked = false;

  loginForm: UntypedFormGroup;
  options: string[];
  filteredLogins: Observable<string[]>;

  shouldLinkCase: boolean;

  get email() {
    return this.loginForm.get('email');
  }

  get password() {
    return this.loginForm.get('password');
  }

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly userService: UserService,
    private readonly authService: AuthService,
    private readonly stateComplaintService: StateComplaintService,
    private readonly mediationService: MediationService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private breakpointObserver: BreakpointObserver
  ) {}

  isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset).pipe(
    map((result) => result.matches),
    shareReplay()
  );

  ngOnInit(): void {
    const param = this.route.snapshot.queryParams;
    this.loginForm = this.fb.group({
      email: ['', { validators: [Validators.required, Validators.email] }],
      password: ['', { validators: [Validators.required] }],
    });

    if (param?.feature && param?.contactId) {
      this.shouldLinkCase = true;
      this.loginForm.controls.email.patchValue(param.email);
      this.loginForm.addControl('feature', this.fb.control(param.feature));
      this.loginForm.addControl('contactId', this.fb.control(param.contactId));
    }

    this.userService.getInteractiveAccessUsers().subscribe((users) => {
      if (users && users.length) {
        this.options = users.map((u) => u.userName).sort();
        this.filteredLogins = this.email.valueChanges.pipe(
          startWith(''),
          map((value) => this.filter(value))
        );
        this.loginForm.get('password').setValue('fake-password');
      }
    });
  }

  private filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.options.filter((option) => option.toLowerCase().includes(filterValue));
  }

  onSubmit() {
    this.loginForm.markAllAsTouched();
    this.loginForm.updateValueAndValidity();

    if (this.loginForm.invalid) {
      return;
    }

    this.loginFailed = false;
    this.isAccountLocked = false;
    this.isBusy = true;

    this.authService.interactiveAccessLogin(this.loginForm.value).subscribe(
      () => {
        if (this.authService.isAuthenticated) {
          if (this.authService.isInteractiveAccessUser && this.route.snapshot.queryParamMap.get('isConnecting')) {
            this.authService.navigateToInteractiveAccess();
          } else if (this.authService.isInteractiveAccessUser) {
            this.authService.navigateToDisputeResolutionDashboard();
          } else {
            console.log('Not an Interactive Access user');
            this.loginFailed = true;
          }
        }
        this.isBusy = false;
      },
      (err: any) => {
        this.loginFailed = true;
        this.isBusy = false;
        if (err?.error?.message === 'Account Locked') this.isAccountLocked = true;
      }
    );
  }

  getErrorMessage() {
    return this.email.hasError('required') ? 'You must enter a value' : this.email.hasError('email') ? 'Not a valid email' : '';
  }
}
